// batch-66 inkoop-afwijkingen (FO-490) — de pure regels van het afwijkingen-
// register op inkoopfacturen.
//
// Géén DB-calls, géén Fastify, géén `node:*`: alles hier is een pure functie die
// zowel de API als de backoffice (voorbeeldberekening in het formulier) gebruikt.
//
//  1. Statusmachine: OPEN -> GEMELD -> IN_BEHANDELING_LEVERANCIER ->
//     CREDITNOTA_VERWACHT -> OPGELOST (met uitkomst), elke overgang met reden.
//  2. Afwijkingsbedrag: berekend uit verwacht/gefactureerd, of handmatig.
//  3. De afgeleide stand van een factuur en de betaalblokkade.
//  4. De referentie AFW-{jaar}-{volgnr} (bouwen, lezen, terugvinden in tekst).
//  5. De creditnota-matcher: welke open afwijkingen horen bij deze creditnota.
//  6. Van 3-way-match-oordeel naar afwijking.
//  7. Extractie-markeringen die een afwijking zijn (duplicaat, verkeerde BV).
//  8. Het beheerbare mailsjabloon.
//  9. Regel-herankering na het vervangen van de factuurregels.
//
// Geld rekent in hele centen (integers); de DB bewaart Decimal.

import {
  INKOOP_AFWIJKING_INVOER_SOORT,
  INKOOP_AFWIJKING_MAIL_REFERENTIE_VELD,
  INKOOP_AFWIJKING_STATUS_LABELS,
  type InkoopAfwijkingSoort,
  type InkoopAfwijkingStatus,
  type InkoopAfwijkingUitkomst,
} from '@streep/types'
import type { InkoopMatchRegelOordeelValue } from './inkoop-match'

// ────────────────────────────────────────────────────────────────────────────
// 1. Statusmachine
// ────────────────────────────────────────────────────────────────────────────

/**
 * De toegestane overgangen. Vooruit overslaan mag (een leverancier die aan de
 * telefoon direct een creditnota toezegt), afsluiten kan vanuit elke open
 * status (accepteren of intrekken hoeft niet eerst gemeld te zijn) en een
 * opgeloste afwijking kan met reden worden heropend.
 */
export const INKOOP_AFWIJKING_TRANSITIES: Record<InkoopAfwijkingStatus, readonly InkoopAfwijkingStatus[]> = {
  OPEN: ['GEMELD', 'IN_BEHANDELING_LEVERANCIER', 'CREDITNOTA_VERWACHT', 'OPGELOST'],
  GEMELD: ['IN_BEHANDELING_LEVERANCIER', 'CREDITNOTA_VERWACHT', 'OPGELOST'],
  IN_BEHANDELING_LEVERANCIER: ['CREDITNOTA_VERWACHT', 'OPGELOST'],
  CREDITNOTA_VERWACHT: ['IN_BEHANDELING_LEVERANCIER', 'OPGELOST'],
  OPGELOST: ['OPEN'],
}

export function isAfwijkingOvergangToegestaan(van: InkoopAfwijkingStatus, naar: InkoopAfwijkingStatus): boolean {
  return INKOOP_AFWIJKING_TRANSITIES[van].includes(naar)
}

/** Staat de afwijking nog open (alles behalve OPGELOST)? */
export function isAfwijkingOpen(status: InkoopAfwijkingStatus): boolean {
  return status !== 'OPGELOST'
}

export type AfwijkingOvergangFout =
  | 'ZELFDE_STATUS'
  | 'NIET_TOEGESTAAN'
  | 'UITKOMST_VERPLICHT'
  | 'UITKOMST_NIET_TOEGESTAAN'
  | 'REDEN_VERPLICHT'
  | 'CREDITNOTA_VEREIST'

export interface AfwijkingOvergangInvoer {
  van: InkoopAfwijkingStatus
  naar: InkoopAfwijkingStatus
  uitkomst?: InkoopAfwijkingUitkomst | null
  reden?: string | null
  /** Er is een creditnota (of correctiefactuur) aan de afwijking gekoppeld. */
  heeftOplossingFactuur?: boolean
}

export type AfwijkingOvergangOordeel =
  | { ok: true }
  | { ok: false; code: AfwijkingOvergangFout; message: string; veld: 'naar' | 'uitkomst' | 'reden' }

/** Minimale lengte van de reden bij een overgang; accepteren vraagt er meer. */
export const AFWIJKING_REDEN_MIN = 3
export const AFWIJKING_ACCEPTATIE_REDEN_MIN = 5

/**
 * De statusmachine: mag deze overgang, met deze uitkomst en reden? Eén plek
 * voor alle regels, zodat een handmatige overgang, de mail-koppeling en de
 * creditnota-koppeling nooit uiteenlopen.
 *
 *  - de overgang moet in de tabel staan;
 *  - elke overgang draagt een reden (FO-490 §2.7);
 *  - OPGELOST vraagt een uitkomst, elke andere status juist geen;
 *  - de uitkomst Creditnota kan alleen met een gekoppelde creditnota;
 *  - accepteren vraagt een reden van minstens vijf tekens (zoals accepteerMatch).
 */
export function beoordeelAfwijkingOvergang(invoer: AfwijkingOvergangInvoer): AfwijkingOvergangOordeel {
  const { van, naar } = invoer
  if (van === naar) {
    return {
      ok: false,
      code: 'ZELFDE_STATUS',
      message: `De afwijking staat al op ${INKOOP_AFWIJKING_STATUS_LABELS[naar]}.`,
      veld: 'naar',
    }
  }
  if (!isAfwijkingOvergangToegestaan(van, naar)) {
    return {
      ok: false,
      code: 'NIET_TOEGESTAAN',
      message: `Van ${INKOOP_AFWIJKING_STATUS_LABELS[van]} naar ${INKOOP_AFWIJKING_STATUS_LABELS[naar]} is niet toegestaan.`,
      veld: 'naar',
    }
  }
  const uitkomst = invoer.uitkomst ?? null
  if (naar === 'OPGELOST' && !uitkomst) {
    return { ok: false, code: 'UITKOMST_VERPLICHT', message: 'Kies hoe de afwijking is opgelost.', veld: 'uitkomst' }
  }
  if (naar !== 'OPGELOST' && uitkomst) {
    return {
      ok: false,
      code: 'UITKOMST_NIET_TOEGESTAAN',
      message: 'Een uitkomst hoort alleen bij de status Opgelost.',
      veld: 'uitkomst',
    }
  }
  if (uitkomst === 'CREDITNOTA' && !invoer.heeftOplossingFactuur) {
    return {
      ok: false,
      code: 'CREDITNOTA_VEREIST',
      message: 'Koppel eerst de creditnota; dan wordt het creditbedrag gecontroleerd.',
      veld: 'uitkomst',
    }
  }
  const reden = (invoer.reden ?? '').trim()
  const minimum = uitkomst === 'GEACCEPTEERD_MET_REDEN' ? AFWIJKING_ACCEPTATIE_REDEN_MIN : AFWIJKING_REDEN_MIN
  if (reden.length < minimum) {
    return {
      ok: false,
      code: 'REDEN_VERPLICHT',
      message: `Geef een reden op (minstens ${minimum} tekens).`,
      veld: 'reden',
    }
  }
  return { ok: true }
}

/**
 * De status waarin een afwijking na een reactie van de leverancier terechtkomt.
 * Alleen een GEMELDE afwijking gaat naar IN_BEHANDELING_LEVERANCIER. Een nog
 * niet gemelde afwijking (OPEN) verandert niet: de leverancier kan niet reageren
 * op iets wat hem niet gemeld is, en de afwijking zou anders uit de meldbundel
 * vallen. Staat hij al in behandeling (of verder), dan verandert er ook niets.
 */
export function statusNaLeverancierReactie(huidig: InkoopAfwijkingStatus): InkoopAfwijkingStatus | null {
  return huidig === 'GEMELD' ? 'IN_BEHANDELING_LEVERANCIER' : null
}

/** Maildomeinen van openbare aanbieders: daar zegt het domein niets over de afzender. */
const OPENBARE_MAILDOMEINEN: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'hotmail.nl',
  'live.com',
  'live.nl',
  'msn.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'proton.me',
  'protonmail.com',
  'ziggo.nl',
  'kpnmail.nl',
  'kpnplanet.nl',
  'planet.nl',
  'xs4all.nl',
  'home.nl',
  'hetnet.nl',
  'upcmail.nl',
  'outlook.nl',
  'hotmail.be',
  'live.be',
  'yahoo.nl',
  'yahoo.co.uk',
  'aol.com',
  'gmx.com',
  'gmx.net',
  'gmx.de',
  'web.de',
  't-online.de',
  'mail.com',
  'pm.me',
  'tutanota.com',
  'zoho.com',
  'yandex.com',
  'telenet.be',
  'skynet.be',
  'casema.nl',
  'chello.nl',
  'quicknet.nl',
  'online.nl',
  'tele2.nl',
  'telfort.nl',
  'solcon.nl',
  'zeelandnet.nl',
  'caiway.nl',
  'kabelfoon.nl',
  'hccnet.nl',
  'wxs.nl',
  'versatel.nl',
  'wanadoo.nl',
  'orange.nl',
  'onsbrabantnet.nl',
  'delta.nl',
])

const domeinVanAdres = (adres: string): string | null => {
  const at = adres.lastIndexOf('@')
  return at > 0 && at < adres.length - 1 ? adres.slice(at + 1).trim().toLowerCase() : null
}

/**
 * Komt deze mail van de leverancier aan wie de afwijking gemeld is? Ja bij
 * exact een bekend adres (waar de melding naartoe ging, of een adres van de
 * relatie), of bij hetzelfde bedrijfsdomein. Een openbaar maildomein (gmail,
 * outlook) telt alleen bij een exact adres. Zo kan een afzender die alleen het
 * referentienummer raadt de afwijking van een ander niet beïnvloeden.
 *
 * `alleenExact` zijn adressen die WEL exact tellen maar nooit als domeinbron: de
 * afzender van de factuurmail kan een facturatieplatform zijn (één domein voor
 * duizenden bedrijven), en dan zou elke klant van dat platform "de leverancier"
 * zijn.
 */
export function isReactieVanLeverancier(
  afzender: string | null | undefined,
  bekendeAdressen: readonly string[],
  alleenExact: readonly string[] = [],
): boolean {
  const van = (afzender ?? '').trim().toLowerCase()
  if (!van) return false
  const schoon = (lijst: readonly string[]): string[] =>
    lijst.map((a) => a.trim().toLowerCase()).filter((a) => a.length > 0)
  const bekend = schoon(bekendeAdressen)
  if (bekend.includes(van) || schoon(alleenExact).includes(van)) return true
  const domein = domeinVanAdres(van)
  if (!domein || OPENBARE_MAILDOMEINEN.has(domein)) return false
  return bekend.some((a) => domeinVanAdres(a) === domein)
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Afwijkingsbedrag
// ────────────────────────────────────────────────────────────────────────────

// BigInt via de constructor i.p.v. literals, zoals indexatie.ts (geen ES2020-
// literal-eis voor wie dit bestand transpileert).
const NUL = BigInt(0)
const TWEE = BigInt(2)

/**
 * a x b in hele centen, exact. `a` heeft hoogstens vier decimalen (prijs),
 * `b` hoogstens drie (aantal): beide worden als geheel getal geschaald en met
 * BigInt vermenigvuldigd, zodat 12,50 x 3 nooit 37,49999 wordt.
 */
function maalInCenten(a: number, b: number): number {
  const aSchaal = BigInt(Math.round(a * 10_000))
  const bSchaal = BigInt(Math.round(b * 10_000))
  // product heeft schaal 10^8; centen = product / 10^6, half-weg-van-nul afgerond.
  const product = aSchaal * bSchaal
  const deler = BigInt(1_000_000)
  const negatief = product < NUL
  const abs = negatief ? -product : product
  const afgerond = (abs * TWEE + deler) / (deler * TWEE)
  const centen = Number(afgerond)
  return negatief ? -centen : centen
}

/** Euro (getal of decimale string) naar hele centen, half-weg-van-nul afgerond. */
export function afwijkingCenten(bedrag: number | string | null | undefined): number {
  if (bedrag === null || bedrag === undefined) return 0
  const n = typeof bedrag === 'number' ? bedrag : Number(String(bedrag).replace(',', '.'))
  if (!Number.isFinite(n)) return 0
  return Math.sign(n) * Math.round(Math.abs(n) * 100)
}

/** Hele centen naar een euro-getal met twee decimalen. */
export function afwijkingEuro(centen: number): number {
  return Math.round(centen) / 100
}

export interface AfwijkingBedragInvoer {
  soort: InkoopAfwijkingSoort
  verwachtWaarde: number | null
  gefactureerdWaarde: number | null
  aantal: number | null
  prijsPerStuk: number | null
  /** Het regel- of factuurbedrag excl. btw waarop de afwijking betrekking heeft. */
  grondslag: number | null
}

/**
 * Het afwijkingsbedrag (excl. btw) in centen, of null wanneer de invoer te
 * onvolledig is om te rekenen (dan is een handmatig bedrag nodig).
 *
 *  - PRIJS / KORTING_ONTBREEKT: (gefactureerde - afgesproken stuksprijs) x aantal
 *  - AANTAL_* / NIET_GELEVERD:  (gefactureerd - verwacht aantal) x stuksprijs
 *  - KWALITEIT_RETOUR:          geretourneerd aantal x stuksprijs
 *  - VERKEERDE_BTW:             grondslag x (gefactureerd - verwacht btw-%) / 100
 *  - DUBBEL / VERKEERDE_BV:     de hele grondslag (regel- of factuurbedrag)
 *  - OVERIG:                    altijd handmatig
 */
export function berekenAfwijkingsBedragCenten(invoer: AfwijkingBedragInvoer): number | null {
  const { verwachtWaarde: v, gefactureerdWaarde: g, aantal, prijsPerStuk, grondslag } = invoer
  switch (INKOOP_AFWIJKING_INVOER_SOORT[invoer.soort]) {
    case 'PRIJS':
      if (v === null || g === null || aantal === null) return null
      return maalInCenten(g - v, aantal)
    case 'AANTAL': {
      if (g === null || prijsPerStuk === null) return null
      // Niet geleverd zonder verwacht aantal = er is niets geleverd. Bij een
      // aantalsverschil is het verwachte aantal verplicht: zonder die waarde zou
      // stil het hele regelbedrag betwist worden.
      const verwacht = v ?? (invoer.soort === 'NIET_GELEVERD' ? 0 : null)
      if (verwacht === null) return null
      return maalInCenten(prijsPerStuk, g - verwacht)
    }
    case 'RETOUR':
      if (aantal === null || prijsPerStuk === null) return null
      return maalInCenten(prijsPerStuk, aantal)
    case 'BTW':
      if (v === null || g === null || grondslag === null) return null
      return maalInCenten(grondslag, (g - v) / 100)
    case 'GEHEEL':
      return grondslag === null ? null : afwijkingCenten(grondslag)
    case 'VRIJ':
      return null
  }
}

export interface AfwijkingBedragOordeel {
  /** Ingevuld = de afwijking mag zo niet worden opgeslagen. */
  blokkade: string | null
  /** Ingevuld = toegestaan, maar de gebruiker hoort dit te zien. */
  waarschuwing: string | null
}

/**
 * FO-490 §5.1 — een afwijkingsbedrag boven het regelbedrag is een waarschuwing,
 * boven het factuurbedrag een blokkade. Vergelijkt absolute waarden (een
 * creditregel is negatief). Geen regel = alleen de factuurgrens.
 */
export function beoordeelAfwijkingsBedrag(invoer: {
  bedragCenten: number
  regelTotaalCenten: number | null
  factuurBedragCenten: number
}): AfwijkingBedragOordeel {
  const bedrag = Math.abs(invoer.bedragCenten)
  if (bedrag > Math.abs(invoer.factuurBedragCenten)) {
    return {
      blokkade: 'Het afwijkingsbedrag is hoger dan het factuurbedrag (excl. btw). Controleer de invoer.',
      waarschuwing: null,
    }
  }
  if (invoer.regelTotaalCenten !== null && bedrag > Math.abs(invoer.regelTotaalCenten)) {
    return {
      blokkade: null,
      waarschuwing: 'Het afwijkingsbedrag is hoger dan het bedrag van deze regel. Klopt dat?',
    }
  }
  return { blokkade: null, waarschuwing: null }
}

// ────────────────────────────────────────────────────────────────────────────
// 3. Stand van de factuur + betaalblokkade
// ────────────────────────────────────────────────────────────────────────────

/** Blokkeert deze afwijking (soort + status) betaling? */
export function isBlokkerendeAfwijking(
  soort: InkoopAfwijkingSoort,
  status: InkoopAfwijkingStatus,
  blokkerendeSoorten: readonly InkoopAfwijkingSoort[],
): boolean {
  return isAfwijkingOpen(status) && blokkerendeSoorten.includes(soort)
}

export interface AfwijkingVoorStand {
  status: InkoopAfwijkingStatus
  soort: InkoopAfwijkingSoort
  /** Excl. btw. Negatief = een afwijking in ons voordeel (telt niet als betwist bedrag). */
  bedragCenten: number
  /** Het btw-percentage van de regel (of van de factuur); null = geen btw. */
  btwPercentage: number | null
}

export interface AfwijkingenStand {
  aantalOpen: number
  openBedragCenten: number
  aantalBlokkerend: number
  blokkerendBedragCenten: number
  /** Het betwiste deel incl. btw (alleen de blokkerende afwijkingen). */
  betwistInclBtwCenten: number
  /** Het onbetwiste deel incl. btw: wat nu betaald mag worden. */
  onbetwistCenten: number
  betalenGeblokkeerd: boolean
}

/**
 * De afgeleide stand van een factuur ("n open afwijkingen, EUR bedrag") en of
 * betalen geblokkeerd is. Een al betaalde factuur blokkeert nooit meer (FO-490
 * §5.2: de afwijking kan nog, de uitkomst is dan een creditnota of terugvordering).
 */
export function berekenAfwijkingenStand(invoer: {
  afwijkingen: readonly AfwijkingVoorStand[]
  /** Factuurtotaal incl. btw. */
  factuurTotaalCenten: number
  /**
   * Factuurbedrag excl. btw. Bekend = een factuur waarvan het hele bedrag is
   * betwist heeft geen onbetwist restant (geen afrondingsrest uit een afgeleid
   * btw-percentage).
   */
  factuurBedragCenten?: number
  blokkerendeSoorten: readonly InkoopAfwijkingSoort[]
  factuurBetaald: boolean
}): AfwijkingenStand {
  let aantalOpen = 0
  let openBedragCenten = 0
  let aantalBlokkerend = 0
  let blokkerendBedragCenten = 0
  let betwistInclBtwCenten = 0
  let betwisteGrondslagCenten = 0
  for (const a of invoer.afwijkingen) {
    if (!isAfwijkingOpen(a.status)) continue
    const betwist = Math.max(0, a.bedragCenten)
    aantalOpen += 1
    openBedragCenten += betwist
    if (!invoer.blokkerendeSoorten.includes(a.soort)) continue
    aantalBlokkerend += 1
    blokkerendBedragCenten += betwist
    // Een verkeerd btw-tarief IS al een btw-bedrag: daar komt geen btw overheen.
    if (a.soort === 'VERKEERDE_BTW') {
      betwistInclBtwCenten += betwist
    } else {
      betwisteGrondslagCenten += betwist
      betwistInclBtwCenten += Math.round(betwist * (1 + (a.btwPercentage ?? 0) / 100))
    }
  }
  const totaal = invoer.factuurTotaalCenten
  if (totaal > 0) {
    const excl = invoer.factuurBedragCenten
    if (excl !== undefined && excl > 0 && betwisteGrondslagCenten >= excl) betwistInclBtwCenten = totaal
    // Het betwiste deel van een factuur is nooit groter dan de factuur zelf.
    betwistInclBtwCenten = Math.min(betwistInclBtwCenten, totaal)
  }
  // Een creditnota (negatief totaal) kent geen onbetwist deel om te betalen.
  const onbetwistCenten = totaal <= 0 ? totaal : Math.max(0, totaal - betwistInclBtwCenten)
  return {
    aantalOpen,
    openBedragCenten,
    aantalBlokkerend,
    blokkerendBedragCenten,
    betwistInclBtwCenten,
    onbetwistCenten,
    betalenGeblokkeerd: !invoer.factuurBetaald && aantalBlokkerend > 0,
  }
}

const EURO = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })

/** "EUR 1.234,56" in NL-notatie, vanuit centen. */
export function formatAfwijkingBedrag(centen: number): string {
  return EURO.format(centen / 100)
}

/** De blokkade-tekst voor de betaalgate (gebruikt door `betaalBlokkades`). */
export function afwijkingBetaalBlokkadeTekst(aantal: number, bedragCenten: number): string {
  const wat = aantal === 1 ? 'Er staat 1 afwijking open' : `Er staan ${aantal} afwijkingen open`
  return `${wat} (${formatAfwijkingBedrag(bedragCenten)} excl. btw). Los die eerst op, of betaal alleen het onbetwiste deel.`
}

/** De blokkade-tekst voor een factuur die door een correctiefactuur is vervangen. */
export function afwijkingVervangenBlokkadeTekst(correctieFactuurNr: string): string {
  return `Deze factuur is vervangen door correctiefactuur ${correctieFactuurNr}. Betaal de correctiefactuur en annuleer deze factuur.`
}

// ────────────────────────────────────────────────────────────────────────────
// 4. Referentie AFW-{jaar}-{volgnr}
// ────────────────────────────────────────────────────────────────────────────

export const AFWIJKING_REFERENTIE_PREFIX = 'AFW'

/** "AFW-2026-0001" voor het volgende volgnummer in het kalenderjaar. */
export function buildAfwijkingReferentie(jaar: number, hoogsteVolgnr: number): string {
  return `${AFWIJKING_REFERENTIE_PREFIX}-${jaar}-${String(hoogsteVolgnr + 1).padStart(4, '0')}`
}

/** Het volgnummer uit "AFW-2026-0007" (0 als de tekst geen referentie is). */
export function parseAfwijkingReferentieNr(referentie: string): number {
  const m = /^AFW-\d{4}-(\d{1,9})$/i.exec(referentie.trim())
  return m ? Number(m[1]) : 0
}

/**
 * Alle afwijkingsreferenties in een tekst (onderwerp van een mail, omschrijving
 * van een creditnota), genormaliseerd naar hoofdletters en ontdubbeld. Tolerant
 * voor spaties of een ander streepje rond de delen ("AFW 2026 0012",
 * "afw-2026-12"): het volgnummer wordt naar vier cijfers aangevuld.
 */
export function vindAfwijkingReferenties(tekst: string | null | undefined): string[] {
  if (!tekst) return []
  const gevonden = new Set<string>()
  const re = /AFW[\s\-_/]{0,2}(\d{4})[\s\-_/]{0,2}(\d{1,9})(?!\d)/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(tekst)) !== null) {
    gevonden.add(`${AFWIJKING_REFERENTIE_PREFIX}-${m[1]}-${String(Number(m[2])).padStart(4, '0')}`)
  }
  return [...gevonden]
}

// ────────────────────────────────────────────────────────────────────────────
// 5. Creditnota-matcher
// ────────────────────────────────────────────────────────────────────────────

export interface CreditnotaKandidaat {
  id: string
  referentie: string
  meldingReferentie: string | null
  /** Het nummer van de factuur waarop de afwijking staat. */
  factuurNr: string
  /** Het (positieve) afwijkingsbedrag excl. btw. */
  bedragCenten: number
}

export type CreditnotaVoorstelReden = 'REFERENTIE' | 'FACTUURNUMMER' | 'BEDRAG' | 'LEVERANCIER'
export type CreditnotaVoorstelZekerheid = 'HOOG' | 'MIDDEL' | 'LAAG'

export interface CreditnotaVoorstel {
  afwijkingId: string
  reden: CreditnotaVoorstelReden
  zekerheid: CreditnotaVoorstelZekerheid
  /** Voorgeselecteerd in het koppelscherm (de gebruiker beslist altijd zelf). */
  voorgesteld: boolean
}

function normaliseerVoorZoeken(tekst: string): string {
  return tekst.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

const ZEKERHEID_RANG: Record<CreditnotaVoorstelZekerheid, number> = { HOOG: 0, MIDDEL: 1, LAAG: 2 }

/**
 * Welke open afwijkingen van deze leverancier horen bij deze creditnota?
 *
 * Volgorde van bewijs (sterk naar zwak):
 *  1. de afwijkingsreferentie (of die van de melding) staat op de creditnota;
 *  2. het nummer van de betwiste factuur staat op de creditnota;
 *  3. het bedrag komt overeen: één afwijking met exact het creditbedrag, of alle
 *     open afwijkingen van één factuur die samen het creditbedrag vormen;
 *  4. anders: een open afwijking bij deze leverancier (kandidaat, niet voorgesteld).
 *
 * Voorgesteld wordt alleen de sterkste groep. De matcher koppelt nooit zelf: hij
 * levert een voorstel, een mens bevestigt.
 */
export function stelCreditnotaKoppelingVoor(invoer: {
  /** Het creditbedrag excl. btw als positief getal (de absolute waarde). */
  creditBedragCenten: number
  /** Factuurnummer, omschrijving, referentienummer en regelteksten van de creditnota. */
  creditTekst: string
  kandidaten: readonly CreditnotaKandidaat[]
}): CreditnotaVoorstel[] {
  const refs = new Set(vindAfwijkingReferenties(invoer.creditTekst))
  const tekst = normaliseerVoorZoeken(invoer.creditTekst)
  const credit = Math.abs(invoer.creditBedragCenten)

  const opReferentie = new Set<string>()
  const opFactuurNr = new Set<string>()
  for (const k of invoer.kandidaten) {
    if (refs.has(k.referentie.toUpperCase()) || (k.meldingReferentie && refs.has(k.meldingReferentie.toUpperCase()))) {
      opReferentie.add(k.id)
      continue
    }
    const nr = normaliseerVoorZoeken(k.factuurNr)
    // Een heel kort nummer ("12") staat vrijwel altijd toevallig in de tekst.
    if (nr.length >= 4 && tekst.includes(nr)) opFactuurNr.add(k.id)
  }

  // Bedrag: precies één afwijking met het creditbedrag, of één factuur waarvan
  // alle open afwijkingen samen het creditbedrag vormen.
  const opBedrag = new Set<string>()
  const exact = invoer.kandidaten.filter((k) => k.bedragCenten === credit)
  if (exact.length === 1) {
    opBedrag.add(exact[0]!.id)
  } else {
    const perFactuur = new Map<string, CreditnotaKandidaat[]>()
    for (const k of invoer.kandidaten) {
      const lijst = perFactuur.get(k.factuurNr) ?? []
      lijst.push(k)
      perFactuur.set(k.factuurNr, lijst)
    }
    const passend = [...perFactuur.values()].filter(
      (lijst) => lijst.length > 1 && lijst.reduce((som, k) => som + k.bedragCenten, 0) === credit,
    )
    if (passend.length === 1) for (const k of passend[0]!) opBedrag.add(k.id)
  }

  const sterkste: ReadonlySet<string> =
    opReferentie.size > 0 ? opReferentie : opFactuurNr.size > 0 ? opFactuurNr : opBedrag

  const voorstellen: CreditnotaVoorstel[] = invoer.kandidaten.map((k) => {
    const voorgesteld = sterkste.has(k.id)
    if (opReferentie.has(k.id)) return { afwijkingId: k.id, reden: 'REFERENTIE', zekerheid: 'HOOG', voorgesteld }
    if (opFactuurNr.has(k.id)) return { afwijkingId: k.id, reden: 'FACTUURNUMMER', zekerheid: 'MIDDEL', voorgesteld }
    if (opBedrag.has(k.id)) return { afwijkingId: k.id, reden: 'BEDRAG', zekerheid: 'MIDDEL', voorgesteld }
    return { afwijkingId: k.id, reden: 'LEVERANCIER', zekerheid: 'LAAG', voorgesteld: false }
  })
  const volgorde = new Map(invoer.kandidaten.map((k, i) => [k.id, i]))
  return voorstellen.sort(
    (a, b) =>
      Number(b.voorgesteld) - Number(a.voorgesteld) ||
      ZEKERHEID_RANG[a.zekerheid] - ZEKERHEID_RANG[b.zekerheid] ||
      (volgorde.get(a.afwijkingId) ?? 0) - (volgorde.get(b.afwijkingId) ?? 0),
  )
}

export interface CreditBedragVergelijking {
  /** Gelijk binnen één cent. */
  gelijk: boolean
  /** Creditbedrag min de som van de afwijkingen (positief = meer gecrediteerd dan betwist). */
  verschilCenten: number
}

/** Vergelijkt het creditbedrag met de som van de te koppelen afwijkingen. */
export function vergelijkCreditBedrag(creditCenten: number, afwijkingenSomCenten: number): CreditBedragVergelijking {
  const verschilCenten = Math.abs(creditCenten) - Math.abs(afwijkingenSomCenten)
  return { gelijk: Math.abs(verschilCenten) <= 1, verschilCenten }
}

/**
 * Verdeelt het creditbedrag over de gekoppelde afwijkingen, in de aangeleverde
 * volgorde: elke afwijking krijgt hoogstens haar EIGEN bedrag. Is de creditnota
 * lager dan de som, dan krijgen de laatste afwijkingen minder (of niets). Is zij
 * hoger, dan blijft het teveel op de creditnota beschikbaar voor andere
 * afwijkingen: het wordt nooit aan de laatste afwijking toegerekend (dan was de
 * creditnota "op" terwijl zij nog een andere afwijking dekt).
 */
export function verdeelCreditOverAfwijkingen(
  creditCenten: number,
  afwijkingen: readonly { id: string; bedragCenten: number }[],
): Map<string, number> {
  const verdeling = new Map<string, number>()
  let rest = Math.abs(creditCenten)
  for (const a of afwijkingen) {
    const deel = Math.min(Math.max(0, a.bedragCenten), rest)
    verdeling.set(a.id, deel)
    rest -= deel
  }
  return verdeling
}

// ────────────────────────────────────────────────────────────────────────────
// 6. Van 3-way-match-oordeel naar afwijking
// ────────────────────────────────────────────────────────────────────────────

export interface MatchRegelVoorAfwijking {
  oordeel: InkoopMatchRegelOordeelValue
  orderPrijs: number | null
  factuurPrijs: number
  ontvangenAantal: number | null
  /** Cumulatief gefactureerd op de orderregel (alle facturen van de order). */
  gefactureerdAantal: number
  /** De factuurregel zelf. */
  regelAantal: number
  regelTotaal: number
}

export interface AfwijkingUitMatch {
  soort: InkoopAfwijkingSoort
  verwachtWaarde: number | null
  gefactureerdWaarde: number | null
  aantal: number | null
  prijsPerStuk: number | null
  bedragCenten: number
}

/** De match-oordelen die een afwijking opleveren (KOPPELING_ONZEKER is intern werk, geen afwijking). */
export const MATCH_OORDEEL_NAAR_AFWIJKING_SOORT: Partial<Record<InkoopMatchRegelOordeelValue, InkoopAfwijkingSoort>> = {
  PRIJSAFWIJKING: 'PRIJS',
  TEVEEL_GEFACTUREERD: 'AANTAL_TE_VEEL',
  FACTUUR_ZONDER_ONTVANGST: 'NIET_GELEVERD',
}

/**
 * Vertaalt een matchregel naar de afwijking die in het register hoort (bron
 * MATCH), of null wanneer het oordeel geen afwijking is. Een teveel-oordeel
 * telt cumulatief over de order; het betwiste bedrag op DEZE factuur is nooit
 * hoger dan het bedrag van de regel zelf.
 */
export function afwijkingUitMatchRegel(r: MatchRegelVoorAfwijking): AfwijkingUitMatch | null {
  const regelTotaalCenten = afwijkingCenten(r.regelTotaal)
  // Een creditregel (of een regel van nul) is nooit een afwijking richting de
  // leverancier: er valt niets te betwisten, en een negatief bedrag zou de
  // sommen in de werkvoorraad en op de leverancierskaart vertekenen. Verschillen
  // op een creditnota lopen via de creditnota-koppeling.
  if (regelTotaalCenten <= 0) return null
  switch (r.oordeel) {
    case 'PRIJSAFWIJKING': {
      if (r.orderPrijs === null) return null
      const bedragCenten = maalInCenten(r.factuurPrijs - r.orderPrijs, r.regelAantal)
      // De match meldt een prijsafwijking in beide richtingen. Een lagere prijs
      // dan besteld is in ons voordeel: daar valt niets te betwisten of te
      // crediteren, dus geen afwijking (de match-acceptatie blijft in het
      // match-paneel).
      if (bedragCenten <= 0) return null
      return {
        soort: 'PRIJS',
        verwachtWaarde: r.orderPrijs,
        gefactureerdWaarde: r.factuurPrijs,
        aantal: r.regelAantal,
        prijsPerStuk: null,
        bedragCenten,
      }
    }
    case 'TEVEEL_GEFACTUREERD': {
      const ontvangen = r.ontvangenAantal ?? 0
      const teveel = maalInCenten(r.factuurPrijs, r.gefactureerdAantal - ontvangen)
      if (teveel <= 0) return null
      return {
        soort: 'AANTAL_TE_VEEL',
        verwachtWaarde: ontvangen,
        gefactureerdWaarde: r.gefactureerdAantal,
        aantal: null,
        prijsPerStuk: r.factuurPrijs,
        bedragCenten: Math.min(teveel, Math.abs(regelTotaalCenten)),
      }
    }
    case 'FACTUUR_ZONDER_ONTVANGST':
      return {
        soort: 'NIET_GELEVERD',
        verwachtWaarde: 0,
        gefactureerdWaarde: r.regelAantal,
        aantal: null,
        prijsPerStuk: r.factuurPrijs,
        bedragCenten: regelTotaalCenten,
      }
    default:
      return null
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 7. Extractie-markeringen
// ────────────────────────────────────────────────────────────────────────────

/**
 * De soort afwijking die bij een extractie-markering hoort, of null. De
 * markeringen zijn vrije tekst in `rawExtractionJson` (geen model): de auto-draft
 * schrijft "Duplicaat: ..." bij een dubbele factuur en "BV onbekend - ..." wanneer
 * de geadresseerde BV niet te herkennen is.
 */
export function markeringAfwijkingSoort(markering: string): InkoopAfwijkingSoort | null {
  const m = markering.trim().toLowerCase()
  if (m.startsWith('duplicaat')) return 'DUBBEL'
  if (m.startsWith('bv onbekend') || m.includes('verkeerde bv') || m.includes('andere bv')) return 'VERKEERDE_BV'
  return null
}

/** Het factuurnummer van het origineel uit een duplicaat-markering, of null. */
export function markeringDuplicaatFactuurNr(markering: string): string | null {
  const viaNummer = /factuurnummer\s+(\S+)\s+bestaat al/i.exec(markering)
  if (viaNummer) return viaNummer[1]!
  const viaBijlage = /\(factuur\s+([^)]+)\)/i.exec(markering)
  return viaBijlage ? viaBijlage[1]!.trim() : null
}

// ────────────────────────────────────────────────────────────────────────────
// 8. Mailsjabloon
// ────────────────────────────────────────────────────────────────────────────

export const STANDAARD_AFWIJKING_MAIL_ONDERWERP = 'Afwijking op factuur [factuurnummer] - [referentie]'

export const STANDAARD_AFWIJKING_MAIL_TEKST = [
  'Geachte heer, mevrouw,',
  '',
  'Bij de controle van uw factuur [factuurnummer] van [factuurdatum] aan [bv] hebben wij het volgende geconstateerd:',
  '',
  '[afwijkingen]',
  '',
  'Het betwiste bedrag is [totaalbedrag] exclusief btw. Wilt u dit controleren en ons een creditnota of een gecorrigeerde factuur sturen? Het onbetwiste deel van de factuur betalen wij volgens de afgesproken termijn.',
  '',
  'Vermeld bij uw reactie de referentie [referentie] in het onderwerp. Dan komt uw bericht direct bij de juiste afwijking terecht.',
  '',
  'Met vriendelijke groet,',
  '[afzender]',
  '[bv]',
].join('\n')

export interface AfwijkingMailRegel {
  referentie: string
  soortLabel: string
  regelOmschrijving: string | null
  /** Al geformatteerd, bv. "EUR 25,00". */
  bedrag: string
  toelichting: string | null
}

export interface AfwijkingMailData {
  referentie: string
  factuurnummer: string
  factuurdatum: string
  leverancier: string
  bv: string
  totaalbedrag: string
  afzender: string
  afwijkingen: readonly AfwijkingMailRegel[]
}

/** De lijst met afwijkingen als platte tekst (één genummerde regel per afwijking). */
export function afwijkingenAlsTekst(regels: readonly AfwijkingMailRegel[]): string {
  return regels
    .map((r, i) => {
      const waar = r.regelOmschrijving ? ` (regel "${r.regelOmschrijving}")` : ''
      const toelichting = r.toelichting ? ` ${r.toelichting}` : ''
      return `${i + 1}. ${r.referentie} - ${r.soortLabel}${waar}: ${r.bedrag} excl. btw.${toelichting}`
    })
    .join('\n')
}

function vulSjabloon(sjabloon: string, data: AfwijkingMailData): string {
  const waarden: Record<string, string> = {
    '[referentie]': data.referentie,
    '[factuurnummer]': data.factuurnummer,
    '[factuurdatum]': data.factuurdatum,
    '[leverancier]': data.leverancier,
    '[bv]': data.bv,
    '[afwijkingen]': afwijkingenAlsTekst(data.afwijkingen),
    '[totaalbedrag]': data.totaalbedrag,
    '[afzender]': data.afzender,
  }
  let uit = sjabloon
  for (const [veld, waarde] of Object.entries(waarden)) uit = uit.split(veld).join(waarde)
  return uit
}

/**
 * Borgt dat de referentie in het onderwerp staat. Zonder referentie kan de
 * reactie van de leverancier niet aan de afwijking gekoppeld worden, dus een
 * onderwerp zonder (door een beheerder of in het meldscherm weggehaald) krijgt
 * hem alsnog achteraan.
 */
export function borgReferentieInOnderwerp(onderwerp: string, referentie: string): string {
  const schoon = onderwerp.replace(/[\r\n]+/g, ' ').trim()
  if (vindAfwijkingReferenties(schoon).includes(referentie.toUpperCase())) return schoon
  return schoon.length > 0 ? `${schoon} - ${referentie}` : referentie
}

/**
 * Onderwerp en tekst van de mail aan de leverancier uit het (beheerbare)
 * sjabloon. Een leeg sjabloon valt terug op de standaardtekst.
 */
export function renderAfwijkingMail(invoer: {
  onderwerpSjabloon: string | null
  tekstSjabloon: string | null
  data: AfwijkingMailData
}): { onderwerp: string; tekst: string } {
  const onderwerpSjabloon = invoer.onderwerpSjabloon?.trim() || STANDAARD_AFWIJKING_MAIL_ONDERWERP
  const tekstSjabloon = invoer.tekstSjabloon?.trim() || STANDAARD_AFWIJKING_MAIL_TEKST
  return {
    onderwerp: borgReferentieInOnderwerp(vulSjabloon(onderwerpSjabloon, invoer.data), invoer.data.referentie),
    tekst: vulSjabloon(tekstSjabloon, invoer.data),
  }
}

/** Zit het verplichte referentieveld in dit onderwerpsjabloon? */
export function onderwerpSjabloonHeeftReferentie(sjabloon: string): boolean {
  return sjabloon.includes(INKOOP_AFWIJKING_MAIL_REFERENTIE_VELD)
}

// ────────────────────────────────────────────────────────────────────────────
// 9. Regel-herankering + ouderdom
// ────────────────────────────────────────────────────────────────────────────

export interface RegelVoorHeranker {
  id: string
  position: number
  omschrijving: string
  /** Het regeltotaal in centen en het aantal in honderdsten, als ze bekend zijn. */
  totaalCenten?: number | null
  aantalHonderdsten?: number | null
}

export type RegelHerankerSoort = 'ZELFDE' | 'GEWIJZIGD' | 'VERDWENEN'

function normaliseerOmschrijving(tekst: string): string {
  return tekst.trim().toLowerCase().replace(/\s+/g, ' ')
}

export interface OudeRegelVoorHeranker {
  /** De id van de oude regel, of een eigen sleutel voor een losse momentopname. */
  id: string
  position: number | null
  omschrijving: string | null
  totaalCenten?: number | null
  aantalHonderdsten?: number | null
}

export interface RegelHerankerKeuze {
  regel: RegelVoorHeranker | null
  soort: RegelHerankerSoort
}

/**
 * Hoe een oude regel aan haar nieuwe regel is gekoppeld: op de id die de client
 * meestuurde (zeker), op inhoud (omschrijving, eventueel met bedrag en aantal), of
 * alleen op positie (er staat een regel op dezelfde plek; of het dezelfde regel
 * is, is niet zeker). `null` = niet gekoppeld.
 */
export type RegelHerankerBron = 'ID' | 'INHOUD' | 'POSITIE'

export interface RegelHerankerKeuzeMetBron extends RegelHerankerKeuze {
  bron: RegelHerankerBron | null
}

/**
 * Is de meegestuurde koppeling (per nieuwe regel de index van de oude regel, of
 * null) bruikbaar? Alleen als zij precies één waarde per nieuwe regel heeft. De
 * aanroeper weet daarmee ook of een VERDWENEN regel MET ZEKERHEID weg is: de
 * client noemde haar dan bij geen enkele nieuwe regel.
 *
 * Een koppeling die bij GEEN ENKELE nieuwe regel een oude regel noemt is niet
 * bruikbaar: dat is vrijwel altijd een client die de sleutels niet meestuurde,
 * niet een review waarin elke regel is vervangen. Zou zij wel gelden, dan waren
 * alle oude regels "met zekerheid weg" en leverde elke bewerkte regel een tweede
 * boete op. De koppeling valt dan terug op de herkenning op inhoud en positie.
 */
export function isRegelHerkomstBruikbaar(
  herkomst: readonly (number | null)[] | null | undefined,
  nieuweRegels: readonly unknown[],
): herkomst is readonly (number | null)[] {
  if (!herkomst || herkomst.length !== nieuweRegels.length) return false
  return herkomst.length === 0 || herkomst.some((h) => h !== null)
}

/**
 * Koppelt de OUDE regels van een factuur 1-op-1 aan de nieuwe. De inkoop-review
 * vervangt bij elke opslag ALLE regels (nieuwe id's); een afwijking of boete
 * volgt haar regel. Elke nieuwe regel krijgt hoogstens één oude regel, en er
 * wordt niet gegokt.
 *
 * Stuurt de client per nieuwe regel mee welke oude regel zij voortzet
 * (`herkomst`), dan is dat de koppeling (stap 0): een regel waarvan alleen de
 * tekst of het bedrag is aangepast blijft dan gewoon dezelfde regel. Zonder die
 * gegevens (een script, een oud tabblad) loopt de koppeling op inhoud. "Inhoud"
 * is de omschrijving PLUS het totaal en het aantal waar die bekend zijn: twee
 * regels "Arbeid" met een ander bedrag zijn verschillende regels.
 *
 *  0. `herkomst` wijst de oude regel aan -> ZELFDE bij gelijke inhoud, anders
 *     GEWIJZIGD (bron ID);
 *  1. dezelfde positie EN dezelfde inhoud -> ZELFDE;
 *  2. daarna, tussen de nog vrije regels, een inhoud die aan BEIDE kanten uniek
 *     is -> ZELFDE (de regel is verschoven);
 *  3. daarna een omschrijving die aan beide kanten uniek is maar met een ander
 *     bedrag of aantal -> GEWIJZIGD (de regel is aangepast en eventueel
 *     verschoven; de verwijzing volgt, maar het hoort gemeld te worden);
 *  4. daarna een nog vrije nieuwe regel op dezelfde positie -> GEWIJZIGD (bron
 *     POSITIE). Is de regel op die positie al de match van een ANDERE oude regel
 *     (er is een regel verwijderd en de rest schoof op), dan volgt de verwijzing
 *     niet. Deze stap vervalt als `herkomst` is meegestuurd: de client zegt dan
 *     zelf dat op die plek een nieuwe regel staat;
 *  5. anders -> VERDWENEN (de verwijzing verliest haar regel; de momentopname
 *     van positie en omschrijving blijft staan).
 *
 * Zelfde aanpak als de doorbelastingen (`matchVervangenRegels`), plus stap 0, 3 en 4.
 *
 * @param herkomst per NIEUWE regel (zelfde volgorde als `nieuweRegels`) de index
 *        in `oudeRegels` van de regel die zij voortzet, of null voor een nieuwe
 *        regel. Alleen gebruikt als de lengte gelijk is aan die van `nieuweRegels`.
 */
export function koppelRegelsMetBron(
  oudeRegels: readonly OudeRegelVoorHeranker[],
  nieuweRegels: readonly RegelVoorHeranker[],
  herkomst?: readonly (number | null)[] | null,
): Map<string, RegelHerankerKeuzeMetBron> {
  type Regel = { omschrijving: string | null; totaalCenten?: number | null; aantalHonderdsten?: number | null }
  const tekstVan = (r: Regel): string | null => (r.omschrijving === null ? null : normaliseerOmschrijving(r.omschrijving))
  const bekend = (x: number | null | undefined): x is number => x !== null && x !== undefined
  /** Zelfde bedrag en aantal; een onbekende waarde aan een van beide kanten telt als gelijk. */
  const zelfdeWaarden = (a: Regel, b: Regel): boolean =>
    (!bekend(a.totaalCenten) || !bekend(b.totaalCenten) || a.totaalCenten === b.totaalCenten) &&
    (!bekend(a.aantalHonderdsten) || !bekend(b.aantalHonderdsten) || a.aantalHonderdsten === b.aantalHonderdsten)
  const zelfdeInhoud = (a: Regel, b: Regel): boolean => {
    const t = tekstVan(a)
    return t !== null && t === tekstVan(b) && zelfdeWaarden(a, b)
  }
  const exact = isRegelHerkomstBruikbaar(herkomst, nieuweRegels)

  const uit = new Map<string, RegelHerankerKeuzeMetBron>()
  const gebruikt = new Set<string>()
  const kies = (o: OudeRegelVoorHeranker, n: RegelVoorHeranker, soort: RegelHerankerSoort, bron: RegelHerankerBron): void => {
    uit.set(o.id, { regel: n, soort, bron })
    gebruikt.add(n.id)
  }

  // 0. De client wijst de oude regel aan.
  if (exact) {
    nieuweRegels.forEach((n, i) => {
      const index = herkomst[i]
      if (index === null || index === undefined || !Number.isInteger(index)) return
      const o = oudeRegels[index]
      if (!o || uit.has(o.id) || gebruikt.has(n.id)) return
      kies(o, n, zelfdeInhoud(o, n) ? 'ZELFDE' : 'GEWIJZIGD', 'ID')
    })
  }
  // 1. Zelfde positie en zelfde inhoud.
  for (const o of oudeRegels) {
    if (uit.has(o.id) || o.position === null) continue
    const n = nieuweRegels.find((x) => !gebruikt.has(x.id) && x.position === o.position && zelfdeInhoud(o, x))
    if (n) kies(o, n, 'ZELFDE', 'INHOUD')
  }
  // 2. Verschoven: de inhoud is aan beide kanten uniek.
  for (const o of oudeRegels) {
    if (uit.has(o.id) || tekstVan(o) === null) continue
    const kandidaten = nieuweRegels.filter((n) => !gebruikt.has(n.id) && zelfdeInhoud(o, n))
    const concurrenten = oudeRegels.filter((x) => !uit.has(x.id) && zelfdeInhoud(o, x))
    if (kandidaten.length === 1 && concurrenten.length === 1) kies(o, kandidaten[0]!, 'ZELFDE', 'INHOUD')
  }
  // 3. Aangepast: de omschrijving is aan beide kanten uniek, het bedrag of aantal niet meer gelijk.
  for (const o of oudeRegels) {
    const tekst = tekstVan(o)
    if (uit.has(o.id) || tekst === null) continue
    const kandidaten = nieuweRegels.filter((n) => !gebruikt.has(n.id) && tekstVan(n) === tekst)
    const concurrenten = oudeRegels.filter((x) => !uit.has(x.id) && tekstVan(x) === tekst)
    if (kandidaten.length === 1 && concurrenten.length === 1) {
      kies(o, kandidaten[0]!, zelfdeWaarden(o, kandidaten[0]!) ? 'ZELFDE' : 'GEWIJZIGD', 'INHOUD')
    }
  }
  // 4. Zelfde positie, andere inhoud (alleen zonder koppeling van de client); 5. anders verdwenen.
  for (const o of oudeRegels) {
    if (uit.has(o.id)) continue
    const opPositie = exact || o.position === null ? undefined : nieuweRegels.find((n) => n.position === o.position)
    if (opPositie && !gebruikt.has(opPositie.id)) kies(o, opPositie, 'GEWIJZIGD', 'POSITIE')
    else uit.set(o.id, { regel: null, soort: 'VERDWENEN', bron: null })
  }
  return uit
}

/**
 * `koppelRegelsMetBron` zonder de bron: per oude regel de nieuwe regel en of zij
 * gelijk, gewijzigd of verdwenen is.
 */
export function koppelRegelsNaVervanging(
  oudeRegels: readonly OudeRegelVoorHeranker[],
  nieuweRegels: readonly RegelVoorHeranker[],
  herkomst?: readonly (number | null)[] | null,
): Map<string, RegelHerankerKeuze> {
  const uit = new Map<string, RegelHerankerKeuze>()
  for (const [id, keuze] of koppelRegelsMetBron(oudeRegels, nieuweRegels, herkomst)) {
    uit.set(id, { regel: keuze.regel, soort: keuze.soort })
  }
  return uit
}

/** Hele dagen tussen twee momenten (nooit negatief). */
export function dagenZonderActiviteit(van: Date, nu: Date): number {
  return Math.max(0, Math.floor((nu.getTime() - van.getTime()) / 86_400_000))
}

// ────────────────────────────────────────────────────────────────────────────
// Getallen in het formulier (NL-notatie)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Leest een getal zoals het in Nederland wordt getypt. Leeg = null; onleesbaar =
 * NaN (het formulier toont dan een veldfout in plaats van de invoer stil als leeg
 * te behandelen).
 *
 *  - met een komma is de komma het decimaalteken en zijn punten duizendtallen:
 *    "12,50" = 12,5 en "1.234,56" = 1234,56;
 *  - zonder komma is "1.250" een duizendtal (1250), maar alleen als het eerste
 *    groepje niet met een nul begint: "0.125" is 0,125;
 *  - elke andere punt is een decimaalteken: "12.5" = 12,5.
 *
 * Het formulier TOONT getallen altijd met een komma (`formatAfwijkingGetal`),
 * zodat een opgeslagen waarde als 1,879 bij het bewerken niet als 1879 terugkomt.
 */
export function parseAfwijkingGetal(invoer: string): number | null {
  const t = (invoer ?? '').trim().replace(/\s/g, '')
  if (t === '') return null
  let norm = t
  if (t.includes(',')) norm = t.replace(/\./g, '').replace(',', '.')
  else if (/^-?[1-9]\d{0,2}(\.\d{3})+$/.test(t)) norm = t.replace(/\./g, '')
  if (!/^-?\d+(\.\d+)?$/.test(norm)) return Number.NaN
  return Number(norm)
}

/** Een getal voor een invoerveld: decimale komma, geen duizendtalpunten. Leeg bij null. */
export function formatAfwijkingGetal(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return ''
  return String(n).replace('.', ',')
}
